// TypeAceLanding.jsx (updated with floating orbs)
import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { 
  ArrowRight, 
  Shield, 
  Target, 
  Gauge, 
  Award,
  Clock,
  Sparkles,
  Keyboard,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Layers,
  Lock,
} from 'lucide-react';
import './TypeAceLanding.css';
import { useNavigate } from 'react-router-dom';

const TypeAceLanding = () => {
  const controls = useAnimation();
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const navi = useNavigate();
  const [theme, setTheme] = useState(localStorage.getItem("typeace-theme") ||'light');
  
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const cycleTheme = () => {
    setTheme(prev => {
      if (prev === 'light'){
        localStorage.setItem("typeace-theme", "dark");
        return 'dark';
      }
      if (prev === 'dark'){
        localStorage.setItem("typeace-theme", "neon");
        return 'neon';
      }
      localStorage.setItem("typeace-theme", "light");
      return 'light';
    });
  };

  useEffect(() => {
    if (isHeroInView) {
      controls.start('visible');
    }
  }, [controls, isHeroInView]);
  
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const comparisonData = [
    {
      other: "Random meaningless text",
      typeace: "Real SSC exam-grade paragraphs",
      icon: <XCircle size={20} />
    },
    {
      other: "No real exam rules",
      typeace: "Strict SSC error penalty formula",
      icon: <XCircle size={20} />
    },
    {
      other: "No error penalties",
      typeace: "Live wrong character highlighting",
      icon: <XCircle size={20} />
    },
    {
      other: "No strict backspace rules",
      typeace: "Strict exam-grade backspace control",
      icon: <XCircle size={20} />
    },
    {
      other: "No anti-cheating",
      typeace: "Tab switch detection • Fullscreen enforcement",
      icon: <XCircle size={20} />
    },
    {
      other: "Inflated WPM scores",
      typeace: "Net & Gross WPM with error penalties",
      icon: <XCircle size={20} />
    }
  ];

  const features = [
    {
      icon: <Shield size={28} />,
      title: "Real Exam Simulation",
      description: "Timed tests with official-style results. Net & Gross WPM tracking with strict accuracy requirements.",
      badge: "SSC Grade"
    },
    {
      icon: <Lock size={28} />,
      title: "Advanced Anti-Cheating",
      description: "Tab switching detection, fullscreen enforcement, copy/paste disabled, and strict key control.",
      badge: "Exam Secure"
    },
    {
      icon: <Gauge size={28} />,
      title: "Live Performance Metrics",
      description: "Real-time WPM, accuracy percentage, error count, and key depression tracking as you type.",
      badge: "Live"
    },
    {
      icon: <Layers size={28} />,
      title: "Multiple Modes",
      description: "Classic practice, fun modes, and strict exam mode with Hindi & English language support.",
      badge: "Flexible"
    },
    {
      icon: <Sparkles size={28} />,
      title: "Premium UI Experience",
      description: "Light, Dark, and Neon themes with smooth transitions and distraction-free layout.",
      badge: "3 Themes"
    },
    {
      icon: <Award size={28} />,
      title: "Realistic Result Sheet",
      description: "Exam-like pressure simulation with detailed breakdown of your performance metrics.",
      badge: "Detailed"
    }
  ];

  const stats = [
    { value: "50,000+", label: "Active Aspirants", icon: <Users size={20} /> },
    { value: "98%", label: "Exam Success Rate", icon: <Target size={20} /> },
    { value: "12+", label: "Exam Patterns", icon: <Award size={20} /> },
    { value: "24/7", label: "Practice Available", icon: <Clock size={20} /> }
  ];

  return (
    <div className="typeace-landing" data-theme={theme}>
      {/* Background Grid Animation */}
      <div className="typeace-grid-bg">
        <div className="typeace-grid-lines"></div>
      </div>
      {/* Subtle Noise Texture */}
      <div className="typeace-noise-overlay"></div>
      {/* Floating Gradient Orbs */}
      <div className="typeace-orb typeace-orb-1"></div>
      <div className="typeace-orb typeace-orb-2"></div>
      <div className="typeace-orb typeace-orb-3"></div>
      <div className="typeace-orb typeace-orb-4"></div>
      {/* Floating Background Elements */}
      <div className="typeace-floating-cursor" />
      <div className="typeace-gradient-bg" />
      {/* Theme indicator - shows current theme */}
      <div className="typeace-theme-indicator">
        <span className="typeace-theme-text">{theme.charAt(0).toUpperCase() + theme.slice(1)} Mode</span>
      </div>
      {/* Navigation */}
      <nav className="typeace-nav">
        <div className="typeace-nav-container">
          <div className="typeace-nav-left">
            {/* Made keyboard icon clickable for theme switching */}
            <button 
              onClick={cycleTheme}
              className="typeace-theme-toggle"
              aria-label="Switch theme"
              title={`Current theme: ${theme}. Click to cycle themes`}
            >
              <Keyboard className="typeace-nav-logo-icon" />
            </button>
            <span className="typeace-nav-logo">TypeAce</span>
          </div>
          <div className="typeace-nav-links">
            <a href="#features">Features</a>
            <a href="#exam">For Exams</a>
            <a href="#pricing">Pricing</a>
          </div>
          <div className="typeace-nav-right">
            <button onClick={() => (navi("/signup"))} className="typeace-nav-btn-primary">Register Institute</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} className="typeace-hero">
        <motion.div
          className="typeace-hero-container"
          variants={staggerContainer}
          initial="hidden"
          animate={controls}
        >
          <motion.div variants={fadeInUp} className="typeace-hero-badge">
            <Sparkles size={14} />
            <span>India's #1 Exam-Grade Typing Platform</span>
          </motion.div>
          <motion.h1 variants={fadeInUp} className="typeace-hero-title">
            Type With Precision.
            <br />
            <span className="typeace-hero-title-gradient">Perform With Confidence.</span>
          </motion.h1>
          <motion.h2 variants={fadeInUp} className="typeace-hero-subtitle">
            SPEED . PRECISION . MASTERY
          </motion.h2>
          <motion.p variants={fadeInUp} className="typeace-hero-description">
            India's most realistic exam-grade typing platform built for serious aspirants 
            and focused learners. Experience the actual pressure of typing hall conditions.
          </motion.p>
          <motion.div variants={fadeInUp} className="typeace-hero-cta">
            <button onClick={() => (navi('/login'))} className="typeace-cta-primary">
              Institute Login
              <ArrowRight size={18} />
            </button>
            <button onClick={() => (navi('/form'))} className="typeace-cta-primary">
              Student Typing
              <ArrowRight size={18} />
            </button>
          </motion.div>
          <motion.div variants={fadeInUp} className="typeace-hero-stats">
            {stats.map((stat, index) => (
              <div key={index} className="typeace-hero-stat">
                <div className="typeace-hero-stat-icon">{stat.icon}</div>
                <div>
                  <div className="typeace-hero-stat-value">{stat.value}</div>
                  <div className="typeace-hero-stat-label">{stat.label}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Comparison Section */}
      <section className="typeace-comparison">
        <div className="typeace-container">
          <motion.div 
            className="typeace-section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="typeace-section-title">Not Just Another Typing Website</h2>
            <p className="typeace-section-subtitle">
              Most typing apps mislead you with inflated scores. TypeAce prepares you for the real exam.
            </p>
          </motion.div>
          <motion.div 
            className="typeace-comparison-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="typeace-comparison-column typeace-other">
              <div className="typeace-comparison-header">
                <XCircle size={24} />
                <h3>Other Typing Apps</h3>
              </div>
              <div className="typeace-comparison-list">
                {comparisonData.map((item, index) => (
                  <motion.div key={index} variants={fadeInUp} className="typeace-comparison-item">
                    <span className="typeace-comparison-icon">{item.icon}</span>
                    <span className="typeace-comparison-text">{item.other}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="typeace-comparison-column typeace-typeace">
              <div className="typeace-comparison-header">
                <CheckCircle2 size={24} />
                <h3>TypeAce</h3>
              </div>
              <div className="typeace-comparison-list">
                {comparisonData.map((item, index) => (
                  <motion.div key={index} variants={fadeInUp} className="typeace-comparison-item typeace-typeace-item">
                    <span className="typeace-comparison-icon typeace-success">✓</span>
                    <span className="typeace-comparison-text">{item.typeace}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
          <motion.div 
            className="typeace-comparison-quote"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <AlertTriangle size={20} />
            <p>
              "If your typing app doesn't simulate real exam rules, it's not preparing you — it's misleading you."
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="typeace-features">
        <div className="typeace-container">
          <motion.div 
            className="typeace-section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="typeace-section-title">Built for Serious Aspirants</h2>
            <p className="typeace-section-subtitle">
              Every feature is designed to simulate real exam conditions and build genuine typing mastery.
            </p>
          </motion.div>
          <motion.div 
            className="typeace-features-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div key={index} variants={fadeInUp} className="typeace-feature-card">
                <div className="typeace-feature-icon">{feature.icon}</div>
                <div className="typeace-feature-badge">{feature.badge}</div>
                <h3 className="typeace-feature-title">{feature.title}</h3>
                <p className="typeace-feature-description">{feature.description}</p>
                <div className="typeace-feature-hover-effect" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="typeace-social-proof">
        <div className="typeace-container">
          <motion.div 
            className="typeace-proof-grid"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <div className="typeace-proof-content">
              <h2>Built for Serious Aspirants</h2>
              <p>
                Designed specifically for SSC & government exam candidates. We focus on realism 
                over gamification, simulating the actual pressure of a typing hall environment.
              </p>
              <ul className="typeace-proof-list">
                <li>
                  <CheckCircle2 size={18} />
                  <span>Real SSC error penalty formula</span>
                </li>
                <li>
                  <CheckCircle2 size={18} />
                  <span>Strict backspace control like real exams</span>
                </li>
                <li>
                  <CheckCircle2 size={18} />
                  <span>Anti-cheating measures you can trust</span>
                </li>
                <li>
                  <CheckCircle2 size={18} />
                  <span>Used by toppers across India</span>
                </li>
              </ul>
            </div>
            <div className="typeace-proof-visual">
              <div className="typeace-proof-stats-card">
                <div className="typeace-proof-stat">
                  <span className="typeace-proof-stat-value">35,000+</span>
                  <span className="typeace-proof-stat-label">SSC Aspirants</span>
                </div>
                <div className="typeace-proof-stat">
                  <span className="typeace-proof-stat-value">98.5%</span>
                  <span className="typeace-proof-stat-label">Accuracy Rate</span>
                </div>
                <div className="typeace-proof-stat">
                  <span className="typeace-proof-stat-value">12</span>
                  <span className="typeace-proof-stat-label">Exam Patterns</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="typeace-final-cta">
        <div className="typeace-container">
          <motion.div 
            className="typeace-cta-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Stop Practicing Randomly.<br />Start Training Realistically.</h2>
            <p>
              Join thousands of serious aspirants who've already discovered the TypeAce difference. 
              Your first test is completely free.
            </p>
            <button onClick={() => (navi("/login"))} className="typeace-cta-glow-button">
              Begin Your First Test
              <ArrowRight size={18} />
            </button>
            <div className="typeace-cta-shine" />
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="typeace-footer">
        <div className="typeace-container">
          <div className="typeace-footer-grid">
            <div className="typeace-footer-brand">
              <Keyboard size={24} />
              <span>TypeAce</span>
              <p>Speed. Precision. Mastery.</p>
            </div>
            <div className="typeace-footer-links">
              <div className="typeace-footer-column">
                <h4>Product</h4>
                <a href="#">Features</a>
                <a href="#">Exam Modes</a>
                <a href="#">Pricing</a>
              </div>
              <div className="typeace-footer-column">
                <h4>Resources</h4>
                <a href="#">Blog</a>
                <a href="#">Guides</a>
                <a href="#">Support</a>
              </div>
              <div className="typeace-footer-column">
                <h4>Legal</h4>
                <a href="#">Privacy</a>
                <a href="#">Terms</a>
                <a href="#">Security</a>
              </div>
            </div>
          </div>
          <div className="typeace-footer-bottom">
            <p>© 2025 TypeAce. All rights reserved. Built for serious aspirants.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Missing import for Users icon
const Users = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg"
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round" 
    strokeLinejoin="round"
    {...props}
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export default TypeAceLanding;